import { Check } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { COUNTDOWN_TYPE_LABELS, nextAnniversary, treeCountdowns, type CountdownType, type Upcoming } from '../../model/countdown';
import { lsGet, lsSet } from '../../model/db';
import { birthDate, isDeceased } from '../../model/people';
import { PersonLink } from '../components/common';
import { useApp } from '../store';

const HIDDEN_KEY = 'relationships.countdownsHidden';
const LONG_PRESS_MS = 500;
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

function loadHidden(): CountdownType[] {
  try {
    const saved = JSON.parse(lsGet(HIDDEN_KEY) ?? '[]');
    return Array.isArray(saved) ? saved : [];
  } catch {
    return [];
  }
}

const RANGES: { days: number; label: string }[] = [
  { days: 30, label: 'Next 30 days' },
  { days: 90, label: 'Next 3 months' },
  { days: 365, label: 'Next year' },
];

const dayFmt = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
const monthFmt = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** Today, then the rest of this week, then one section per month. */
function sectionOf(u: Upcoming): string {
  if (u.days === 0) return 'Today';
  if (u.days < 7) return 'This week';
  return monthFmt.format(u.date);
}

/** Birthdays, anniversaries, and remembrance days coming up, soonest first. */
export function CountdownsView() {
  const tree = useApp((s) => s.tree)!;
  const [hidden, setHidden] = useState<CountdownType[]>(loadHidden);
  const [range, setRange] = useState(365);
  const all = useMemo(() => treeCountdowns(tree, range), [tree, range]);
  // Offer every kind the tree has in the coming year, so the choices don't change with the range.
  const yearCounts = useMemo(() => {
    const counts = new Map<CountdownType, number>();
    for (const u of treeCountdowns(tree, 365)) counts.set(u.type, (counts.get(u.type) ?? 0) + 1);
    return counts;
  }, [tree]);
  const types = (Object.keys(COUNTDOWN_TYPE_LABELS) as CountdownType[]).filter((t) => yearCounts.has(t));
  const save = (next: CountdownType[]) => {
    setHidden(next);
    lsSet(HIDDEN_KEY, JSON.stringify(next));
  };
  const toggle = (t: CountdownType) => save(hidden.includes(t) ? hidden.filter((x) => x !== t) : [...hidden, t]);
  /**
   * Show just this kind (in every tree, so "only birthdays" means the same thing everywhere).
   * Doing it again on the kind that's already alone brings everything back.
   */
  const only = (t: CountdownType) => {
    const alone = !hidden.includes(t) && types.every((x) => x === t || hidden.includes(x));
    save(alone ? [] : (Object.keys(COUNTDOWN_TYPE_LABELS) as CountdownType[]).filter((x) => x !== t));
  };
  // Phones have no Option key, so pressing and holding a chip does the same. The click that
  // follows the press is swallowed so it doesn't toggle the chip straight back off.
  const press = useRef<{ timer: number; fired: boolean } | null>(null);
  const endPress = () => {
    if (press.current) clearTimeout(press.current.timer);
  };
  const shown = all.filter((u) => !hidden.includes(u.type));
  const hiddenHere = types.filter((t) => hidden.includes(t));
  const sections = useMemo(() => {
    const out = new Map<string, Upcoming[]>();
    for (const u of shown) {
      const key = sectionOf(u);
      out.set(key, [...(out.get(key) ?? []), u]);
    }
    return [...out];
  }, [shown]);
  // Living people whose birthday can't be counted down to, so nobody wonders where they went.
  const uncounted = useMemo(() => Object.values(tree.people).filter((p) => !isDeceased(p) && birthDate(p) && !nextAnniversary(birthDate(p))).length, [tree]);

  return (
    <div className="page">
      <div className="page-inner">
        <div className="page-head">
          <div>
            <h1 style={{ fontSize: 22 }}>Countdowns</h1>
            <p className="muted" style={{ marginTop: 4 }}>
              Birthdays, anniversaries, and remembrance days coming up in {tree.name}.
            </p>
          </div>
          <select className="select" style={{ width: 'auto' }} value={range} onChange={(e) => setRange(Number(e.target.value))} aria-label="How far ahead">
            {RANGES.map((r) => (
              <option key={r.days} value={r.days}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <div className="row wrap" style={{ alignItems: 'flex-start' }}>
          {types.length > 0 && (
            <div className="toggle-chips" role="group" aria-label="Kinds of dates to show">
              {types.map((t) => {
                const on = !hidden.includes(t);
                const label = COUNTDOWN_TYPE_LABELS[t]!;
                // Lowercase for a sentence, but keep acronyms: “HRT birthdays”, not “hrt birthdays”.
                const lower = /^[A-Z]{2}/.test(label) ? label : label[0].toLowerCase() + label.slice(1);
                return (
                  <button
                    key={t}
                    className="toggle-chip"
                    aria-pressed={on}
                    title={`${on ? 'Hide' : 'Show'} ${lower}. ${isMac ? 'Option' : 'Alt'}-click to show only ${lower}.`}
                    onClick={(e) => {
                      if (press.current?.fired) return void (press.current = null);
                      if (e.altKey) only(t);
                      else toggle(t);
                    }}
                    onKeyDown={(e) => {
                      if (e.altKey && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        only(t);
                      }
                    }}
                    onPointerDown={(e) => {
                      if (e.pointerType !== 'touch') return;
                      endPress();
                      const p = { fired: false, timer: 0 };
                      p.timer = window.setTimeout(() => {
                        p.fired = true;
                        only(t);
                        navigator.vibrate?.(10);
                      }, LONG_PRESS_MS);
                      press.current = p;
                    }}
                    onPointerUp={endPress}
                    onPointerCancel={endPress}
                    onPointerLeave={endPress}
                    onContextMenu={(e) => e.preventDefault()}
                  >
                    {on && <Check size={13} />}
                    {label}
                    <span className="count">{yearCounts.get(t)}</span>
                  </button>
                );
              })}
              {hiddenHere.length > 0 && (
                <button className="add-link" onClick={() => save([])}>
                  Show all
                </button>
              )}
              {types.length > 1 && (
                <div className="chip-hint faint">
                  <span className="for-mouse">{isMac ? 'Option' : 'Alt'}-click a kind to show only that kind.</span>
                  <span className="for-touch">Press and hold a kind to show only that kind.</span>
                </div>
              )}
            </div>
          )}
        </div>

        {sections.length === 0 ? (
          <div className="card-box muted">
            Nothing {hiddenHere.length ? 'you’re showing is ' : ''}coming up in the {RANGES.find((r) => r.days === range)!.label.toLowerCase()}.
          </div>
        ) : (
          <div className="countdown-list">
            {sections.map(([title, items]) => (
              <section key={title} className="card-box">
                <h2 className="report-title">{title}</h2>
                <ul className="countdowns">
                  {items.map((u, i) => (
                    <li key={i} className={u.days === 0 ? 'today' : undefined}>
                      <span className="when">{u.days === 0 ? 'Today' : u.days === 1 ? 'Tomorrow' : `${u.days} days`}</span>
                      <span className="date faint">{dayFmt.format(u.date)}</span>
                      <span className="what">
                        <PersonLink id={u.personId} />
                        {u.otherId && (
                          <>
                            <span className="muted">&</span>
                            <PersonLink id={u.otherId} />
                          </>
                        )}
                        <span className="muted">{u.label}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}

        <p className="faint small-text">
          Only dates with a day, month, and year can be counted down to.
          {uncounted > 0 && !hidden.includes('birthday') && ` ${uncounted} living ${uncounted === 1 ? 'person’s birth date is' : 'people’s birth dates are'} missing the day or month, so ${uncounted === 1 ? 'that birthday isn’t' : 'those birthdays aren’t'} shown.`}
        </p>
      </div>
    </div>
  );
}
